import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import Chip from '@mui/material/Chip';
import { PageHero } from '@/components/PageHero';
import { NotifyForm } from '@/components/NotifyForm';
import { phases, ACTIVE_PHASE_INDEX } from '@/content/roadmap';
import { useSeo } from '@/seo/useSeo';

function phaseStatus(index: number): 'done' | 'active' | 'upcoming' {
  if (index < ACTIVE_PHASE_INDEX) return 'done';
  if (index === ACTIVE_PHASE_INDEX) return 'active';
  return 'upcoming';
}

// borderClass/dotClass are literal Tailwind arbitrary-value classes (not interpolated), so the
// JIT compiler's static source scan finds them — kept alongside `color` (still used for the two
// MUI `sx` spots below, which compile to a CSS class via emotion rather than an inline `style`
// attribute) so the three statuses' visuals stay in one place.
const statusMeta = {
  done: {
    label: 'Complete',
    color: '#3ddc84',
    borderClass: 'border-[#3ddc84]',
    dotClass: 'bg-[#3ddc84]',
    Icon: CheckCircleIcon,
  },
  active: {
    label: 'In progress',
    color: '#b06bff',
    borderClass: 'border-[#b06bff]',
    dotClass: 'bg-[#b06bff]',
    Icon: RadioButtonCheckedIcon,
  },
  upcoming: {
    label: 'Planned',
    color: 'rgba(255,255,255,0.35)',
    borderClass: 'border-[rgba(255,255,255,0.35)]',
    dotClass: 'bg-[rgba(255,255,255,0.35)]',
    Icon: RadioButtonUncheckedIcon,
  },
};

export function Roadmap(): JSX.Element {
  useSeo({
    title: 'Roadmap',
    description:
      'Where Nexus is today, phase by phase — Foundation, Core modules, Private beta, and General availability. Updated by hand, not a marketing schedule.',
    path: '/roadmap',
  });
  return (
    <div>
      <PageHero
        eyebrow="Building in the open"
        title="Where Nexus actually is, right now."
        subtitle="Not a marketing timeline — a rough, honestly-updated guide to what's shipped, what's being built, and what's next. A module is only marked done when the real path is wired and tested."
      />

      <section className="pb-16 md:pb-24">
        <div className="container-content">
          <div className="relative mx-auto max-w-3xl">
            <div className="absolute left-[19px] top-2 bottom-2 w-px bg-white/10 md:left-[23px]" />
            <div className="flex flex-col gap-10">
              {phases.map((phase, i) => {
                const status = phaseStatus(i);
                const meta = statusMeta[status];
                const StatusIcon = meta.Icon;
                return (
                  <div key={phase.name} className="relative flex gap-5 md:gap-6">
                    <div
                      className={`z-10 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border bg-[#0b0b13] md:h-12 md:w-12 ${meta.borderClass}`}
                    >
                      <StatusIcon fontSize="small" sx={{ color: meta.color }} />
                    </div>
                    <div
                      className={`flex-1 rounded-2xl border p-6 md:p-7 ${
                        status === 'active'
                          ? 'border-nexus-purple/40 bg-nexus-purple/[0.06]'
                          : 'border-white/10 bg-white/[0.02]'
                      }`}
                    >
                      <div className="flex flex-wrap items-center gap-3">
                        <h2 className="font-display text-xl font-bold text-nexus-ink md:text-2xl">{phase.name}</h2>
                        <Chip
                          label={meta.label}
                          size="small"
                          className="h-[22px] border text-[10.5px] font-bold"
                          sx={{ borderColor: meta.color, color: meta.color, backgroundColor: 'transparent' }}
                        />
                      </div>
                      <p className="mt-3 text-sm text-white/70">{phase.summary}</p>
                      <ul className="mt-5 flex flex-col gap-2.5">
                        {phase.milestones.map((m) => (
                          <li key={m} className="flex items-start gap-2.5 text-sm text-white/65">
                            <span className={`mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full ${meta.dotClass}`} />
                            {m}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 md:py-24">
        <div className="container-content">
          <div className="rounded-[28px] border border-nexus-purple/35 bg-gradient-to-br from-[#2a1450] to-[#120a26] p-8 text-center md:p-16">
            <h2 className="font-display text-3xl font-bold text-nexus-ink md:text-[36px]">
              Want the next phase in your inbox?
            </h2>
            <p className="mx-auto mt-3 max-w-[520px] text-white/70">
              We&apos;ll reach out as Nexus moves into private beta — no spam, no lists sold.
            </p>
            <div className="mt-8 flex justify-center">
              <NotifyForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
